import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { MasterDataFieldDefinitionsApi } from './api';
import type {
  CreateMasterDataFieldDefinitionDto,
  MasterDataFieldDefinition,
  UpdateMasterDataFieldDefinitionDto,
} from './types';

const QUERY_KEYS = {
  all: ['master-data-field-definitions'] as const,
  list: (orgId: string, dataset: string) =>
    [...QUERY_KEYS.all, orgId, dataset] as const,
};

// Shared with queryClient.fetchQuery callers that need a dataset's definitions on demand.
export const fieldDefinitionsQuery = (orgId: string, dataset: string) => ({
  queryKey: QUERY_KEYS.list(orgId, dataset),
  queryFn: () => MasterDataFieldDefinitionsApi.list(orgId, dataset),
});

export const useFieldDefinitions = (orgId: string, dataset: string) => {
  return useQuery({
    ...fieldDefinitionsQuery(orgId, dataset),
    enabled: !!orgId && !!dataset,
  });
};

/**
 * Field definitions for several datasets at once, keyed by dataset name — for a UI
 * with N independent dataset pickers (e.g. one writeback policy per row, each
 * targeting its own dataset) where hooks can't be called in a loop. Shares the same
 * query key as useFieldDefinitions, so a dataset already cached from a single-dataset
 * picker elsewhere on the same panel isn't re-fetched.
 */
export const useFieldDefinitionsForDatasets = (orgId: string, datasets: string[]): Record<string, MasterDataFieldDefinition[]> => {
  const uniqueDatasets = Array.from(new Set(datasets.filter(Boolean)));
  const results = useQueries({
    queries: uniqueDatasets.map((dataset) => ({
      queryKey: QUERY_KEYS.list(orgId, dataset),
      queryFn: () => MasterDataFieldDefinitionsApi.list(orgId, dataset),
      enabled: !!orgId && !!dataset,
    })),
  });
  const byDataset: Record<string, MasterDataFieldDefinition[]> = {};
  uniqueDatasets.forEach((dataset, i) => {
    byDataset[dataset] = results[i]?.data ?? [];
  });
  return byDataset;
};

export const useCreateFieldDefinition = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateMasterDataFieldDefinitionDto) =>
      MasterDataFieldDefinitionsApi.create(orgId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateFieldDefinition = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, dto }: { fieldId: number; dto: UpdateMasterDataFieldDefinitionDto }) =>
      MasterDataFieldDefinitionsApi.update(orgId, fieldId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteFieldDefinition = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: number) => MasterDataFieldDefinitionsApi.remove(orgId, fieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export { QUERY_KEYS as fieldDefinitionQueryKeys };
